import { useState, useEffect } from 'react';
import { GroupTable } from '../components/common/GroupTable';
import { NotReadyYet } from '../components/common/NotReadyYet';
import { getGroups, getSettings } from '../lib/api';
import { Trophy, Loader2, Award } from 'lucide-react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export const GironiPage = () => {
  const [groups, setGroups] = useState([]);
  const [settings, setSettings] = useState(null);
  const [thirds, setThirds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [groupsRes, settingsRes, thirdsRes] = await Promise.all([
          getGroups(),
          getSettings(),
          axios.get(`${API}/standings/thirds`).catch(() => ({ data: [] })),
        ]);
        setGroups(groupsRes.data);
        setSettings(settingsRes.data);
        setThirds(thirdsRes.data || []);
      } catch (error) {
        console.error('Error fetching groups:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="gironi-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!settings?.draw_completed) {
    return (
      <NotReadyYet
        title="Sorteggio non ancora effettuato"
        message="I gironi e le classifiche saranno disponibili dopo il sorteggio ufficiale. Resta connesso!"
      />
    );
  }

  const qualifyingThirdIds = thirds.filter(t => t.qualifies).map(t => t.id);
  // Evidenzia in tempo reale chi è attualmente in zona qualificazione (1ª, 2ª e 2 migliori 3°)
  // appena almeno una partita è stata giocata
  const groupPhaseComplete = groups.some(g => (g.teams_data || []).some(t => (t.played || 0) > 0));

  return (
    <div className="py-8 px-4" data-testid="gironi-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-4">
            <Trophy className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">
              Fase a Gironi
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter">
            CLASSIFICHE <span className="text-primary">GIRONI</span>
          </h1>
          <p className="mt-3 text-slate-400">
            3 gironi da 4 squadre - Passano le prime 2 di ogni girone + le 2 migliori 3ª classificate
          </p>
        </div>

        {groups.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-6">
            {groups.map(group => (
              <GroupTable
                key={group.id}
                group={group}
                teams={group.teams_data || []}
                qualifyingThirdIds={qualifyingThirdIds}
                anyMatchPlayed={groupPhaseComplete}
              />
            ))}
          </div>
        ) : (
          <div className="glass-card p-12 text-center">
            <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Nessun girone ancora generato.</p>
          </div>
        )}

        {/* Classifica delle terze */}
        {thirds.length > 0 && groupPhaseComplete && (
          <div className="mt-10 glass-card p-4 sm:p-6" data-testid="thirds-standings">
            <div className="flex items-center gap-3 mb-4">
              <Award className="w-5 h-5 text-gold" />
              <h2 className="font-heading text-lg sm:text-xl font-bold">
                CLASSIFICA <span className="text-gold">3ª CLASSIFICATE</span>
              </h2>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Ranking: punti · differenza reti · fair play (cartellini, meno è meglio). Le prime 2 si qualificano ai quarti.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-800">
                    <th className="py-2 pr-2 w-10">#</th>
                    <th className="py-2 pr-2">Squadra</th>
                    <th className="py-2 pr-2">Girone</th>
                    <th className="py-2 pr-2 text-center w-12">PT</th>
                    <th className="py-2 pr-2 text-center w-12">DR</th>
                    <th className="py-2 pr-2 text-center w-12">FP</th>
                    <th className="py-2 pr-2 text-right w-24">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {thirds.map((t) => (
                    <tr key={t.id} className={t.qualifies ? 'bg-gold/5' : ''} data-testid={`third-row-${t.id}`}>
                      <td className="py-2 pr-2 font-numbers font-bold">{t.rank}</td>
                      <td className="py-2 pr-2 font-heading font-semibold uppercase">{t.name}</td>
                      <td className="py-2 pr-2 text-slate-400">Girone {t.group_name}</td>
                      <td className="py-2 pr-2 text-center font-numbers text-primary font-bold">{t.points}</td>
                      <td className="py-2 pr-2 text-center font-numbers">
                        <span className={t.goal_difference > 0 ? 'text-green-500' : t.goal_difference < 0 ? 'text-red-500' : ''}>
                          {t.goal_difference > 0 ? '+' : ''}{t.goal_difference}
                        </span>
                      </td>
                      <td className="py-2 pr-2 text-center font-numbers text-slate-400">{t.fair_play}</td>
                      <td className="py-2 pr-2 text-right">
                        {t.qualifies ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold/20 text-gold font-bold uppercase">
                            Qualificata
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-500">Eliminata</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GironiPage;
